import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import Icon from './AppIcon';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';

// Pages where the coach widget should be hidden
const HIDDEN_ON_PATHS = ['/interview', '/login', '/signup', '/auth', '/certificate', '/live'];


const SUGGESTIONS_INITIAL = [
  "What should I prepare next?",
  "What are my weak areas?",
  "Suggest an interview for me",
  "How can I improve my score?",
];

const CoachWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'coach',
      text: "Hi! I'm your prepxai Coach 👋 I have access to your interview history and weak areas. Ask me anything about your prep!",
      suggestion: null,
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const featureFlags = useFeatureFlags();
  
  if (!featureFlags || featureFlags.loading) return null;
  const { isEnabled } = featureFlags;

  if (!isEnabled('ai_coach')) return null;

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (open && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  // Focus input when opening
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  const [articleContext, setArticleContext] = useState(null);
  const articleContextRef = useRef(null);
  
  // Listen for article context updates
  useEffect(() => {
    const handleContextUpdate = (e) => {
      const { title, content, open: shouldOpen } = e.detail || {};
      if (title) {
        const ctx = { title, content };
        setArticleContext(ctx);
        articleContextRef.current = ctx;
        // Set an initial message if it's a new article
        setMessages([
          {
            role: 'coach',
            text: `Hi! I'm your prepxai Tutor for "${title}". Ask me anything about this topic! 📚`,
            suggestion: null,
          }
        ]);
        if (shouldOpen) setOpen(true);
      } else {
        setArticleContext(null);
        articleContextRef.current = null;
      }
    };
    
    window.addEventListener('prep-coach-context-set', handleContextUpdate);
    return () => window.removeEventListener('prep-coach-context-set', handleContextUpdate);
  }, []);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');

    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setLoading(true);

    try {
      const currentContext = articleContextRef.current;
      const payload = { 
        message: msg,
        article_title: currentContext?.title,
        article_content: currentContext?.content
      };
      const res = await api.post('/api/coach/chat', payload);
      const { message, suggestion } = res.data;
      setMessages(prev => [...prev, { role: 'coach', text: message, suggestion }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'coach',
        text: "Sorry, I'm having trouble right now. Please try again in a moment.",
        suggestion: null,
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Listen for text selection from ArticleReader
  useEffect(() => {
    const handleSelectionAsk = (e) => {
      const { text, open: shouldOpen } = e.detail || {};
      if (text) {
        if (shouldOpen !== false) setOpen(true);
        const prompt = `Explain this: "${text}"`;
        // Instead of waiting in the input box, we automatically submit it!
        sendMessage(prompt);
      }
    };

    window.addEventListener('prep-coach-ask-selection', handleSelectionAsk);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => window.removeEventListener('prep-coach-ask-selection', handleSelectionAsk);
  }, []);

  const handleStartInterview = (suggestion) => {
    navigate('/interview-setup', {
      state: {
        prefill: {
          interviewType: suggestion.interview_type,
          topics: suggestion.topics || [],
        }
      }
    });
    setOpen(false);
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 ${open ? 'bg-slate-700 scale-90' : 'bg-slate-900 hover:scale-110'
          }`}
        aria-label="Open AI Coach"
      >
        {open
          ? <Icon name="X" size={20} color="#fff" />
          : (
            <>
              <Icon name="Sparkles" size={22} color="#fff" />
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-20 pointer-events-none" />
            </>
          )
        }
      </button>

      {/* Chat window */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[360px] max-h-[560px] flex flex-col rounded-3xl shadow-2xl bg-white border border-slate-100 overflow-hidden"
          style={{ boxShadow: '0 24px 80px rgba(99,102,241,0.18), 0 4px 20px rgba(0,0,0,0.1)' }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3.5 bg-slate-900">
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
              <Icon name="Sparkles" size={16} color="#34d399" />
            </div>
            <div className="flex-1">
              <p className="text-white font-black text-[13px] leading-tight">prepxai coach</p>
              <div className="flex items-center gap-1 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <p className="text-slate-400 text-[10px] font-medium">AI-powered</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white transition-colors">
              <Icon name="ChevronDown" size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 scrollbar-thin scrollbar-thumb-slate-200"
            style={{ maxHeight: '360px' }}>
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] ${msg.role === 'user' ? '' : 'space-y-2'}`}>
                  {/* Bubble */}
                  <div className={`px-3.5 py-2.5 rounded-2xl text-[12px] leading-relaxed font-medium ${msg.role === 'user'
                    ? 'bg-slate-900 text-white rounded-br-sm'
                    : 'bg-slate-50 border border-slate-100 text-slate-700 rounded-bl-sm'
                    }`}>
                    {msg.text}
                  </div>

                  {/* Suggestion card */}
                  {msg.suggestion?.type === 'start_interview' && (
                    <button
                      onClick={() => handleStartInterview(msg.suggestion)}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 bg-gradient-to-r from-emerald-500/10 to-emerald-400/5 border border-emerald-200 rounded-xl hover:border-emerald-400 hover:bg-emerald-50 transition-all group mt-2"
                    >
                      <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                        <Icon name="Play" size={12} className="text-emerald-600" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-[11px] font-black text-emerald-700">
                          Start {msg.suggestion.interview_type} Interview
                        </p>
                        {msg.suggestion.topics?.length > 0 && (
                          <p className="text-[9px] text-emerald-600/70 font-medium mt-0.5">
                            Topics: {msg.suggestion.topics.join(', ')}
                          </p>
                        )}
                      </div>
                      <Icon name="ArrowRight" size={13} className="text-emerald-500 group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  )}

                  {msg.suggestion?.type === 'navigate' && (
                    <button
                      onClick={() => {
                        navigate(msg.suggestion.url);
                        setOpen(false);
                      }}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-gradient-to-r from-blue-500/10 to-blue-400/5 border border-blue-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all group mt-2"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                          <Icon name="Compass" size={12} className="text-blue-600" />
                        </div>
                        <span className="text-[11px] font-black text-blue-700">{msg.suggestion.label}</span>
                      </div>
                      <Icon name="ArrowRight" size={13} className="text-blue-500 group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {loading && (
              <div className="flex justify-start">
                <div className="px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl rounded-bl-sm">
                  <div className="flex gap-1 items-center h-4">
                    {[0, 1, 2].map(i => (
                      <span
                        key={i}
                        className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce"
                        style={{ animationDelay: `${i * 0.15}s` }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Quick suggestions — only shown initially */}
          {messages.length === 1 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5">
              {SUGGESTIONS_INITIAL.map(s => (
                <button
                  key={s}
                  onClick={() => sendMessage(s)}
                  disabled={loading}
                  className="px-2.5 py-1 bg-emerald-50 border border-emerald-100 text-emerald-700 text-[10px] font-bold rounded-full hover:bg-emerald-100 transition-colors truncate max-w-full"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="px-3 pb-3 pt-1 border-t border-slate-100 bg-white">
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-2xl px-3 py-2 focus-within:border-emerald-400 focus-within:bg-white transition-all">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                placeholder="Ask your coach..."
                disabled={loading}
                className="flex-1 bg-transparent text-[12px] text-slate-700 placeholder-slate-400 outline-none font-medium"
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                className="w-7 h-7 rounded-full flex items-center justify-center bg-slate-900 disabled:opacity-40 hover:opacity-80 transition-all shrink-0"
              >
                <Icon name="Send" size={13} color="#fff" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CoachWidget;
